import { cellText } from '../../lib/format.js';
import { useDrill } from '../../lib/DrillContext.jsx';
import { orderRows, orderPivots, orderColumns, orderPivotMeasures, dimensionLikeCalcs, orderPivotColumns, pivotHeaderRuns } from '../../lib/lookerOrder.js';
import { visibleColumns, visibleForPivot, hiddenFieldSet } from '../../lib/visibleFields.mjs';
import {
  ROW_TOTAL_KEY, hasColumnTotals, hasRowTotals, columnTotal, realPivots, rowTotal, rowTotalMeasures, totalText,
} from '../../lib/tableTotals.js';

// Data table — drawn the way the Looker tile draws it (tickets #27 / #50 / #126:
// "import means copy, not reinterpret"). Uses Looker's per-field `align` and
// `rendered` strings so numbers match the source exactly. Handles pivoted
// queries (one column group per pivot value × measure), drill links, and:
//   · columns in Looker's order, pinned columns first (lib/lookerOrder.js);
//   · hidden columns — whole fields, one measure under one pivot, or a whole
//     pivot group (lib/visibleFields.mjs);
//   · a dimension-only table calculation beside the dimensions, not repeated
//     (blank) under every pivot;
//   · Looker's totals: the Total row (`totals_data`) and a pivoted table's Total
//     column (`$$$_row_total_$$$`), each only when the tile's own query asked
//     Looker for them and its vis doesn't hide them (lib/tableTotals.js);
//   · row numbers, when the tile has Looker's "Show row numbers" on.
// `query` (the tile's saved Looker query) is optional: with it, rows follow the
// tile's sort (lib/lookerOrder.js) and totals follow its flags.
export default function TableTile({ data, visConfig = {}, query = null }) {
  const { openDrill, canDrill } = useDrill();
  const fields = data.fields || {};
  // `pulse_order` is the server saying which sort actually ran — it isn't always
  // the tile's own (server/rowOrder.js).
  const rows = orderRows(data.data || [], fields, query, data.pulse_order?.sorts);
  // The row-total entry Looker adds to `pivots` is drawn as the Total column,
  // never as an ordinary column group.
  const allPivots = realPivots(data.pivots || []);
  const shown = visibleColumns(fields, visConfig);
  // Pivoted: a dimension-only calc is one plain cell per row — it sits with the
  // dimensions, as in Looker.
  const hidden = hiddenFieldSet(visConfig);
  const dimCalcs = allPivots.length ? dimensionLikeCalcs((fields.table_calculations || []).filter((c) => !hidden.has(c?.name)), data.data) : [];
  const dimCalcNames = new Set(dimCalcs.map((c) => c.name));
  const { dimensions, measures, all: allFields } = orderColumns(
    [...shown.dimensions, ...dimCalcs],
    shown.measures.filter((m) => !dimCalcNames.has(m.name)),
    visConfig, query,
  );
  const pivots = orderPivots(allPivots, visConfig, measures.map((m) => m.name));
  const rowNumbers = visConfig.show_row_numbers === true;
  const showTotals = hasColumnTotals(data, visConfig);
  const totalsLabel = visConfig.totals_label || 'Total';

  if (!rows.length || (!dimensions.length && !measures.length)) return <Empty />;

  // Column headings as Looker writes them: a renamed column (series_labels)
  // wins; with "Show full field name" on (`show_view_names: true`) the heading
  // carries the view name too ("Tickets Count"), else the short label ("Count").
  const fullNames = visConfig.show_view_names === true;
  const mLabel = (m) => visConfig.series_labels?.[m.name] || (fullNames ? m.label : m.label_short) || m.label || m.label_short || m.name;
  const pLabel = (p) => (p.data ? Object.values(p.data).join(' / ') : p.key);

  const drillCell = (cell, titleParts) => {
    if (!canDrill(cell?.links)) return {};
    return {
      onClick: () => openDrill(cell.links, titleParts.filter(Boolean).join(' · ')),
      style: drillStyle,
    };
  };

  // Sticky left edge: the row-number column (when shown) and the first data
  // column both stay in view when a wide table scrolls sideways on a phone.
  const firstLeft = rowNumbers ? ROWNUM_W : 0;
  const bgOf = (i) => (i % 2 ? 'var(--row-stripe)' : 'var(--tile-bg)');
  const numHead = () => rowNumbers && <th style={{ ...thStyle, ...rowNumCell, ...stickyHeadCorner }} aria-label="Row number" />;
  const numCell = (i) => rowNumbers && <td style={{ ...tdStyle, ...rowNumCell, ...stickyCol(bgOf(i), 0) }}>{i + 1}</td>;
  const numFoot = () => rowNumbers && <td style={{ ...tfootTd, ...rowNumCell, ...stickyFootCorner }} />;

  // ─── Pivoted table: dimension columns + (pivot × measure) column groups ──────
  if (pivots.length > 0 && measures.length > 0) {
    // Hide what Looker hides per pivot (hidden_pivots): one measure under one
    // pivot (e.g. "% change" under KFF26 only), or the whole pivot group.
    const groups = pivots
      .map((p) => ({ p, ms: orderPivotMeasures(visibleForPivot(measures, visConfig, p.key), visConfig, p.key) }))
      .filter((g) => g.ms.length > 0);
    // The Total column, only when Looker computes AND draws it. Looker stores its
    // own row-total setting as "right"; a bare `true` is what older Pulse imports
    // wrote from the grid's default vis flags — Looker never drew those, so
    // neither does Pulse (a re-sync replaces them with Looker's own value).
    const rowTotalsAsked = !query || (typeof query.row_total === 'string' && query.row_total !== '');
    const rtMeasures = rowTotalsAsked && hasRowTotals(data, visConfig) ? rowTotalMeasures(data, measures) : [];
    const pivotFoot = showTotals && (
      groups.some((g) => g.ms.some((m) => columnTotal(data, m.name, g.p.key)))
      || rtMeasures.some((m) => columnTotal(data, m.name, ROW_TOTAL_KEY))
    );

    // Left to right, one column at a time — Looker's column_order can split a
    // pivot's columns apart (lib/lookerOrder.js orderPivotColumns).
    const cols = orderPivotColumns(groups, visConfig);
    const runs = pivotHeaderRuns(cols);
    // A divider wherever the pivot changes from one column to the next.
    const startsRun = cols.map((c, i) => i > 0 && cols[i - 1].p.key !== c.p.key);
    // The pivot field's own name ("Events") sits above the dimension headers,
    // where Looker puts it.
    const pivotFieldLabel = (fields.pivots || []).map((f) => mLabel(f)).filter(Boolean).join(' / ');

    // In-cell data bars (Looker series_cell_visualizations) — precompute each
    // active column's max so bar widths are relative to the column.
    const cellViz = visConfig.series_cell_visualizations || {};
    const colMax = {};
    for (const { p, m } of cols) {
      if (!cellViz[m.name]?.is_active) continue;
      let mx = 0;
      for (const row of rows) { const v = Math.abs(Number(row[m.name]?.[p.key]?.value)); if (Number.isFinite(v)) mx = Math.max(mx, v); }
      colMax[`${p.key}.${m.name}`] = mx;
    }

    return (
      <Scroll>
        <table style={tableStyle}>
          <thead>
            <tr>
              {rowNumbers && <th style={{ ...thStyle, ...rowNumCell, ...stickyHeadCorner }} aria-label="Row number" />}
              {dimensions.length > 0 && (
                <th colSpan={dimensions.length} style={{ ...thStyle, textAlign: 'right', color: 'var(--muted)', ...stickyHeadCorner, left: firstLeft }}>{pivotFieldLabel}</th>
              )}
              {runs.map((r, ri) => (
                <th key={`${r.p.key}.${ri}`} colSpan={r.span} style={{ ...thStyle, textAlign: 'center', borderLeft: ri ? '1px solid var(--hairline)' : undefined }}>
                  {pLabel(r.p)}
                </th>
              ))}
              {rtMeasures.length > 0 && (
                <th colSpan={rtMeasures.length} style={{ ...thStyle, textAlign: 'center', borderLeft: '2px solid var(--border)' }}>{totalsLabel}</th>
              )}
            </tr>
            <tr>
              {rowNumbers && <th style={{ ...thStyle, ...rowNumCell, top: 28, ...stickyHeadCorner }} aria-label="Row number" />}
              {dimensions.map((d, di) => (
                <th key={d.name} style={{ ...thStyle, top: 28, textAlign: 'left', ...(di === 0 ? { ...stickyHeadCorner, left: firstLeft } : null) }}>{mLabel(d)}</th>
              ))}
              {cols.map(({ p, m }, ci) => (
                <th key={`${p.key}.${m.name}`} style={{ ...thStyle, top: 28, textAlign: 'right', borderLeft: startsRun[ci] ? '1px solid var(--hairline)' : undefined }}>
                  {mLabel(m)}
                </th>
              ))}
              {rtMeasures.map((m, mi) => (
                <th key={`rt.${m.name}`} style={{ ...thStyle, top: 28, textAlign: 'right', borderLeft: mi === 0 ? '2px solid var(--border)' : undefined }}>
                  {mLabel(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ background: bgOf(i) }}>
                {numCell(i)}
                {dimensions.map((d, di) => (
                  <td key={d.name} style={{ ...tdStyle, textAlign: 'left', ...(di === 0 ? stickyCol(bgOf(i), firstLeft) : null) }}>{cellText(row[d.name], dimCalcNames.has(d.name) ? d : undefined)}</td>
                ))}
                {cols.map(({ p, m }, ci) => {
                  const cell = row[m.name]?.[p.key];
                  const dr = drillCell(cell, [...dimensions.map((d) => cellText(row[d.name])), pLabel(p), mLabel(m)]);
                  const mx = colMax[`${p.key}.${m.name}`];
                  return (
                    <td key={`${p.key}.${m.name}`} {...dr}
                      style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderLeft: startsRun[ci] ? '1px solid var(--hairline)' : undefined, ...(dr.style || null) }}>
                      {mx ? <Bar value={Number(cell?.value)} max={mx}>{cellText(cell, m)}</Bar> : cellText(cell, m)}
                    </td>
                  );
                })}
                {rtMeasures.map((m, mi) => (
                  <td key={`rt.${m.name}`} style={{ ...tdStyle, ...totalCellStyle, textAlign: 'right', borderLeft: mi === 0 ? '2px solid var(--border)' : undefined }}>
                    {totalText(rowTotal(row, m.name), m)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {pivotFoot && (
            <tfoot>
              <tr>
                {numFoot()}
                {dimensions.map((d, di) => (
                  <td key={d.name} style={{ ...tfootTd, textAlign: 'left', ...(di === 0 ? { ...stickyFootCorner, left: firstLeft } : null) }}>{di === 0 ? totalsLabel : ''}</td>
                ))}
                {cols.map(({ p, m }, ci) => (
                  <td key={`${p.key}.${m.name}`} style={{ ...tfootTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderLeft: startsRun[ci] ? '1px solid var(--hairline)' : undefined }}>
                    {totalText(columnTotal(data, m.name, p.key), m)}
                  </td>
                ))}
                {rtMeasures.map((m, mi) => (
                  <td key={`rt.${m.name}`} style={{ ...tfootTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderLeft: mi === 0 ? '2px solid var(--border)' : undefined }}>
                    {totalText(columnTotal(data, m.name, ROW_TOTAL_KEY), m)}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </Scroll>
    );
  }

  // ─── Flat table ──────────────────────────────────────────────────────────────
  const align = (f) => f.align || (measures.includes(f) ? 'right' : 'left');
  const flatFoot = showTotals && measures.some((m) => columnTotal(data, m.name));
  const drillTitle = (row, f) => {
    const dims = dimensions.map((d) => cellText(row[d.name])).filter((v) => v && v !== '∅');
    const label = f.label_short || f.label;
    return dims.length ? `${dims.join(' · ')} · ${label}` : label;
  };

  return (
    <Scroll>
      <table style={tableStyle}>
        <thead>
          <tr>
            {numHead()}
            {allFields.map((f, fi) => (
              <th key={f.name} style={{ ...thStyle, textAlign: align(f), ...(fi === 0 ? { ...stickyHeadCorner, left: firstLeft } : null) }} title={f.label || f.name}>
                {mLabel(f)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: bgOf(i) }}>
              {numCell(i)}
              {allFields.map((f, fi) => {
                const cell = row[f.name];
                const isMeasure = measures.includes(f);
                const dr = isMeasure ? drillCell(cell, [drillTitle(row, f)]) : {};
                return (
                  <td key={f.name} {...dr}
                    style={{ ...tdStyle, textAlign: align(f), fontVariantNumeric: isMeasure ? 'tabular-nums' : 'normal', ...(fi === 0 ? stickyCol(bgOf(i), firstLeft) : null), ...(dr.style || null) }}>
                    {cellText(cell, isMeasure ? f : undefined)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {flatFoot && (
          <tfoot>
            <tr>
              {numFoot()}
              {allFields.map((f, fi) => {
                const t = measures.includes(f) ? columnTotal(data, f.name) : null;
                return (
                  <td key={f.name} style={{ ...tfootTd, textAlign: t ? align(f) : 'left', fontVariantNumeric: t ? 'tabular-nums' : 'normal', ...(fi === 0 ? { ...stickyFootCorner, left: firstLeft } : null) }}>
                    {t ? totalText(t, f) : (fi === 0 ? totalsLabel : '')}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </Scroll>
  );
}

function Scroll({ children }) {
  // Own the touch gesture so scrolling a wide/tall table works cleanly on mobile:
  // momentum scrolling, the table handles its own pan (touch-action), and
  // overscroll-contain stops a scroll at the table's edge from also dragging the
  // page underneath it.
  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'auto',
      WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y', overscrollBehavior: 'contain',
    }}>
      {children}
    </div>
  );
}

const tableStyle = { borderCollapse: 'collapse', width: '100%', fontSize: 12 };
const thStyle = {
  padding: '6px 10px', background: 'var(--elevated)', borderBottom: '2px solid var(--border)',
  fontWeight: 600, whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2,
};
const tdStyle = { padding: '5px 10px', borderBottom: '1px solid var(--hairline)', whiteSpace: 'nowrap', color: 'var(--text)' };
// Keep the first (label) column pinned while scrolling wide tables sideways —
// matters most on narrow/mobile widths. Corner header sits above everything.
const stickyHeadCorner = { left: 0, zIndex: 3 };
const stickyCol = (bg, left = 0) => ({ position: 'sticky', left, zIndex: 1, background: bg });
// Looker's "Show row numbers" column: narrow, muted, always the left edge.
const ROWNUM_W = 34;
const rowNumCell = { width: ROWNUM_W, minWidth: ROWNUM_W, maxWidth: ROWNUM_W, textAlign: 'right', color: 'var(--muted)', fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box', paddingLeft: 4, paddingRight: 6 };
// Totals: the Total row is pinned to the bottom while the rows scroll under it.
const tfootTd = {
  padding: '6px 10px', background: 'var(--elevated)', borderTop: '2px solid var(--border)',
  fontWeight: 700, whiteSpace: 'nowrap', color: 'var(--text)',
  position: 'sticky', bottom: 0, zIndex: 2,
};
const stickyFootCorner = { position: 'sticky', left: 0, zIndex: 3 };
// Row-total column inside the BODY — same emphasis, no stickiness.
const totalCellStyle = { fontWeight: 700, fontVariantNumeric: 'tabular-nums' };
const drillStyle = { cursor: 'pointer', color: 'var(--brand)', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 };

// In-cell data bar (Looker cell visualization): a proportional bar behind the
// value, growing from the left, with the number on top.
function Bar({ value, max, children }) {
  const pct = max > 0 && Number.isFinite(value) ? Math.max(0, Math.min(100, (Math.abs(value) / max) * 100)) : 0;
  return (
    <div style={{ position: 'relative', minHeight: 15 }}>
      {pct > 0 && (
        <div style={{ position: 'absolute', left: 0, top: 1, bottom: 1, width: `${pct}%`, background: 'rgba(66,133,244,0.22)', borderRadius: 2 }} />
      )}
      <div style={{ position: 'relative' }}>{children}</div>
    </div>
  );
}

function Empty() {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ccc', fontSize: 12 }}>No data</div>;
}
